import React, { useState, useRef, useEffect } from 'react';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase';
import { getBestMatch, SIMILARITY_THRESHOLD } from '../utils/cosineSimilarity';
import { useAuth } from './AuthContext';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Avatar,
  Paper,
  IconButton,
  Slide,
  Badge,
  Button,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon
} from '@mui/icons-material';

// Animated Chatbot SVG Icon
function AnimatedChatbotIcon({ active }) {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: active ? 'bounceGlow 1.2s infinite' : 'none',
        filter: active ? 'drop-shadow(0 0 8px #1976D2)' : 'none',
        '@keyframes bounceGlow': {
          '0%, 100%': { transform: 'translateY(0) scale(1)' },
          '20%': { transform: 'translateY(-8px) scale(1.08)' },
          '40%': { transform: 'translateY(0) scale(1)' },
          '60%': { filter: 'drop-shadow(0 0 16px #1976D2)' },
        },
      }}
    >
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        {/* Outer blue circle */}
        <circle cx="16" cy="16" r="16" fill="#1976D2" />
        {/* Head */}
        <ellipse cx="16" cy="18" rx="10" ry="9" fill="#fff" />
        {/* Ears */}
        <ellipse cx="6" cy="18" rx="2" ry="3" fill="#fff" />
        <ellipse cx="26" cy="18" rx="2" ry="3" fill="#fff" />
        {/* Eyes */}
        <circle cx="13" cy="18" r="1.5" fill="#1976D2" />
        <circle cx="19" cy="18" r="1.5" fill="#1976D2" />
        {/* Mouth */}
        <rect x="13" y="22" width="6" height="2" rx="1" fill="#1976D2" />
      </svg>
    </Box>
  );
}

// ---------- Transition ----------
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ---------- Categories (Aalay-specific) ----------
const questionCategories = {
  general: {
    name: "General",
    questions: [
      "What is Aalay?",
      "How do I get started?",
      "What services do you offer?"
    ]
  },
  warranty: {
    name: "Warranty",
    questions: [
      "How do I register a warranty?",
      "How to check warranty status?",
      "What documents are needed for warranty claim?",
      "How long is the warranty period?"
    ]
  },
  amc: {
    name: "AMC",
    questions: [
      "What is AMC?",
      "How to create an AMC schedule?",
      "How to track AMC maintenance?",
      "What are AMC benefits?"
    ]
  },
  complaint: {
    name: "Complaints",
    questions: [
      "How to file a complaint?",
      "How to track complaint status?",
      "What is the complaint resolution time?",
      "How to escalate a complaint?"
    ]
  },
  membership: {
    name: "Membership",
    questions: [
      "How to become a member?",
      "What are membership benefits?",
      "How to renew membership?",
      "What are membership tiers?"
    ]
  },
  support: {
    name: "Support",
    questions: [
      "How to contact support?",
      "What are support hours?",
      "How to get technical help?",
      "Where to find user guides?"
    ]
  }
};

const serviceTiles = [
  { label: 'Warranty', prompt: 'Tell me about warranty services on this platform.' },
  { label: 'AMC', prompt: 'Tell me about AMC services on this platform.' },
  { label: 'Complaints', prompt: 'Tell me about complaint services on this platform.' },
  { label: 'Membership', prompt: 'Tell me about membership services on this platform.' },
  { label: 'Contracts', prompt: 'Tell me about contract services on this platform.' },
  { label: 'Support', prompt: 'Tell me about support services on this platform.' }
];

// ---------- Knowledge Base ----------
const knowledgeBase = {
  "what is aalay?": "Aalay is an intelligent operations platform by OmneVu.com that helps you manage warranties, AMC schedules, complaints, and memberships all in one place.",
  "how do i get started?": "To get started, sign up for an account, complete your profile, and then you can register warranties, create AMC schedules, file complaints, and manage your memberships.",
  "what services do you offer?": "We offer warranty management, AMC scheduling, complaint tracking, membership management, and contract management services.",
  
  "how do i register a warranty?": "To register a warranty, go to the Warranty section, click on 'Register Warranty', fill in the product details, upload necessary documents, and submit.",
  "how to check warranty status?": "You can check your warranty status by visiting the Warranty Cards section and viewing your registered items.",
  "what documents are needed for warranty claim?": "Typically, you need the purchase invoice, warranty certificate, product serial number, and proof of identity.",
  "how long is the warranty period?": "Warranty periods vary by product and manufacturer. Check your warranty certificate for specific details.",
  
  "what is amc?": "AMC stands for Annual Maintenance Contract. It's a service agreement for regular maintenance of your products or equipment.",
  "how to create an amc schedule?": "Go to the AMC section, click 'New Schedule', fill in the maintenance details, set the frequency, and assign technicians if needed.",
  "how to track amc maintenance?": "You can track AMC maintenance in the AMC Schedules section where you can view upcoming and completed maintenance tasks.",
  "what are amc benefits?": "AMC benefits include regular maintenance, priority service, cost savings on repairs, and extended equipment life.",
  
  "how to file a complaint?": "To file a complaint, go to the Complaints section, click 'File Complaint', describe your issue, attach relevant documents, and submit.",
  "how to track complaint status?": "Track your complaint status in the Complaints section where you can see real-time updates on your submitted complaints.",
  "what is the complaint resolution time?": "Resolution times vary by issue type. Most complaints are resolved within 3-7 business days.",
  "how to escalate a complaint?": "If your complaint isn't resolved within the expected time, you can escalate it through the support team or contact customer service directly.",
  
  "how to become a member?": "To become a member, fill out the membership form, choose your membership tier, complete payment if applicable, and await approval.",
  "what are membership benefits?": "Membership benefits include priority support, exclusive discounts, access to premium features, and dedicated account management.",
  "how to renew membership?": "You can renew your membership from the Membership section before your current membership expires.",
  "what are membership tiers?": "We offer Basic, Premium, and Enterprise membership tiers with varying benefits and pricing.",
  
  "how to contact support?": "You can contact support through the chatbot, email us at support@aalay.com, or call our helpline.",
  "what are support hours?": "Our support team is available Monday to Friday, 9 AM to 6 PM IST.",
  "how to get technical help?": "For technical help, use the chatbot, submit a support ticket, or schedule a call with our technical team.",
  "where to find user guides?": "User guides are available in the Help section of our platform."
};

// ---------- Component ----------
const FloatingChatbot = ({ isLoggedIn = false }) => {
  const { currentUser, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm your Aalay Assistant. Ask me about Warranty, AMC, Complaints, Membership, or Support.",
      sender: 'bot',
      timestamp: new Date(),
      isQuestion: false
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [activeCategory, setActiveCategory] = useState('general');
  const messagesEndRef = useRef(null);
  const isAuthenticated = Boolean(currentUser || isLoggedIn);
  const canShowChatbot = true;

  useEffect(() => {
    if (loading) return;

    setMessages([
      {
        id: 1,
        text: isAuthenticated
          ? "Hi! I'm your Aalay Assistant. Ask me about Warranty, AMC, Complaints, Membership, or Support."
          : "Hi! I'm your Aalay Assistant. Ask me about Warranty, AMC, Complaints, Membership, or Support. For account-specific help, please log in.",
        sender: 'bot',
        timestamp: new Date(),
        isQuestion: false
      }
    ]);
    setActiveCategory('general');
    setInputMessage('');
    setIsTyping(false);
    setHasNewMessage(false);
  }, [isAuthenticated, loading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleOpen = () => { setOpen(true); setHasNewMessage(false); };
  const handleClose = () => setOpen(false);

  const formatTime = (date) => new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const pickKB = (text) => {
    const t = text.toLowerCase();
    if (knowledgeBase[t]) return knowledgeBase[t];
    if (t.includes('warranty')) return knowledgeBase["how do i register a warranty?"];
    if (t.includes('amc')) return knowledgeBase["what is amc?"];
    if (t.includes('complaint')) return knowledgeBase["how to file a complaint?"];
    if (t.includes('membership')) return knowledgeBase["how to become a member?"];
    if (t.includes('support')) return knowledgeBase["how to contact support?"];
    if (/\b(hi|hello|hey)\b/.test(t)) return "Hello! I can help with warranty registration, AMC scheduling, complaint filing, membership management, and support.";
    return null;
  };

  const botReply = async (text) => {
    const normalizedText = text.trim().toLowerCase();
    let found = null;

    if (isAuthenticated) {
      try {
        // STEP 1: Check answered questions in Firestore
        const qSnap = await getDocs(collection(firestore, 'chatbot_questions'));
        for (const doc of qSnap.docs) {
          const data = doc.data();
          if (data?.question && data?.answer) {
            const dbQuestion = data.question.trim().toLowerCase();
            if (dbQuestion.includes(normalizedText) || normalizedText.includes(dbQuestion)) {
              found = data.answer.trim();
              console.log("[Chatbot] Found answer in Firestore");
              break;
            }
          }
        }
      } catch (e) {
        console.error("[Chatbot] Error checking Firestore answered questions:", e);
      }
    }

    // Return if found in Firestore
    if (found) {
      setMessages((prev) => [...prev, {
        id: Date.now(),
        text: found,
        sender: 'bot',
        timestamp: new Date()
      }]);
      return;
    }

    // STEP 2: Check knowledge base
    const kbReply = pickKB(text);
    if (kbReply) {
      console.log("[Chatbot] Found answer in knowledge base");
      setMessages((prev) => [...prev, {
        id: Date.now(),
        text: kbReply,
        sender: 'bot',
        timestamp: new Date()
      }]);
      return;
    }

    // STEP 3: ALWAYS run cosine similarity for all queries
    try {
      console.log("[Chatbot] Running cosine similarity for:", text);
      const { response, score } = await getBestMatch(text);

      console.log("[Chatbot] Similarity score:", score);
      console.log("[Chatbot] Threshold:", SIMILARITY_THRESHOLD);

      // If score meets threshold, use the matched response
      if (score >= SIMILARITY_THRESHOLD) {
        console.log("[Chatbot] Score passed threshold - using matched response");
        setMessages((prev) => [...prev, {
          id: Date.now(),
          text: response,
          sender: 'bot',
          timestamp: new Date(),
          confidence: score
        }]);
        return;
      }

      console.log("[Chatbot] Score below threshold - using fallback");
    } catch (e) {
      console.error("[Chatbot] Error in cosine similarity lookup:", e);
    }

    // STEP 4: Fallback - save as unanswered question
    let fallbackReply = isAuthenticated
      ? "Thanks! We'll get back with an answer soon."
      : "I can help with general Aalay questions before login. 2 log in for account-specific support or to submit a new question to our team.";

    if (isAuthenticated) {
      try {
        const uSnap = await getDocs(collection(firestore, 'unanswered_questions'));
        const alreadyAsked = uSnap.docs.some((doc) => {
          const data = doc.data();
          const dbQuestion = data?.question?.trim().toLowerCase();
          return dbQuestion && (dbQuestion.includes(normalizedText) || normalizedText.includes(dbQuestion));
        });

        if (alreadyAsked) {
          fallbackReply = "We already noted this question earlier. Our team will update with an answer soon.";
        } else {
          await addDoc(collection(firestore, 'unanswered_questions'), {
            question: normalizedText,
            timestamp: new Date().toISOString()
          });
        }
      } catch (e) {
        console.error("[Chatbot] Error saving unanswered question:", e);
      }
    }

    setMessages((prev) => [...prev, {
      id: Date.now(),
      text: fallbackReply,
      sender: 'bot',
      timestamp: new Date()
    }]);
  };

  const simulateBotResponse = (userMessage) => {
    setIsTyping(true);
    setTimeout(async () => {
      if (!isAuthenticated) {
        setMessages((prev) => [...prev, {
          id: Date.now(),
          text: 'Please log in to continue with the chatbot. Once you sign in, I can answer your questions.',
          sender: 'bot',
          timestamp: new Date()
        }]);
        setIsTyping(false);
        if (!open) setHasNewMessage(true);
        return;
      }

      await botReply(userMessage);
      setIsTyping(false);
      if (!open) setHasNewMessage(true);
    }, 900);
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;
    const msg = inputMessage;
    setMessages((prev) => [...prev, { id: Date.now(), text: msg, sender: 'user', timestamp: new Date() }]);
    setInputMessage('');
    simulateBotResponse(msg);
  };

  const handleQuickQuestion = (q) => {
    setMessages((prev) => [...prev, { id: Date.now(), text: q, sender: 'user', timestamp: new Date(), isQuestion: true }]);
    simulateBotResponse(q);
    setActiveCategory(null);
  };

  const renderSuggestedQuestions = () => activeCategory && (
    <Box sx={{ mt: 1.75, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" sx={{ color: 'rgba(15, 23, 42, 0.62)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>{questionCategories[activeCategory]?.name}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {questionCategories[activeCategory]?.questions?.map((q, i) => (
          <Chip key={i} label={q} size="small" onClick={() => handleQuickQuestion(q)} sx={{ borderRadius: '999px', bgcolor: 'rgba(37,99,235,0.08)', color: '#17408B', border: '1px solid rgba(37,99,235,0.12)', '&:hover': { bgcolor: 'rgba(37,99,235,0.14)' } }} />
        ))}
      </Box>
    </Box>
  );

  const renderServiceTiles = () => (
    <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(15, 23, 42, 0.62)',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.8
        }}
      >
        Explore Services
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
        {serviceTiles.map((tile) => (
          <Button
            key={tile.label}
            variant="contained"
            size="small"
            onClick={() => handleQuickQuestion(tile.prompt)}
            sx={{
              minHeight: 42,
              textTransform: 'none',
              borderRadius: '14px',
              fontSize: '0.78rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #0F3D91 0%, #2563EB 100%)',
              boxShadow: '0 10px 24px rgba(37, 99, 235, 0.16)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0B2F70 0%, #1D4ED8 100%)',
              }
            }}
          >
            {tile.label}
          </Button>
        ))}
      </Box>
      <Box
        sx={{
          mt: 0.75,
          borderBottom: '1px solid rgba(148, 163, 184, 0.35)'
        }}
      />
    </Box>
  );

  const renderCategoryButtons = () => (
    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {Object.keys(questionCategories).map((catKey) => (
        <Button key={catKey} variant="outlined" size="small"
          onClick={() => setActiveCategory(catKey)}
          sx={{ textTransform: 'none', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, borderColor: activeCategory === catKey ? '#2563EB' : 'rgba(37,99,235,0.18)', color: activeCategory === catKey ? '#17408B' : '#2563EB', bgcolor: activeCategory === catKey ? 'rgba(37,99,235,0.08)' : 'transparent', px: 1.25, '&:hover': { borderColor: '#2563EB', bgcolor: 'rgba(37,99,235,0.08)' } }}>
          {questionCategories[catKey].name}
        </Button>
      ))}
    </Box>
  );

  if (loading || !canShowChatbot) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <Box sx={{ position: 'fixed', bottom: 32, right: 18, zIndex: 1500, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Box
            sx={{
              mb: 0.5,
              px: 1.6,
              py: 0.65,
              borderRadius: '999px',
              background: 'linear-gradient(135deg, rgba(37,99,235,0.98), rgba(14,116,144,0.98))',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.95rem',
              letterSpacing: 0.2,
              boxShadow: '0 12px 30px rgba(37, 99, 235, 0.26)',
              animation: 'fadePulse 2s infinite',
              '@keyframes fadePulse': {
                '0%': { opacity: 0, transform: 'scale(0.95)' },
                '20%': { opacity: 1, transform: 'scale(1.05)' },
                '50%': { opacity: 1, transform: 'scale(1)' },
                '80%': { opacity: 1, transform: 'scale(1.05)' },
                '100%': { opacity: 0, transform: 'scale(0.95)' },
              },
            }}
          >
            Need help?
          </Box>
          <Badge color="error" variant="dot" invisible={!hasNewMessage}>
            <Fab
              onClick={handleOpen}
              sx={{
                width: 68,
                height: 68,
                background: 'linear-gradient(135deg, #2563EB 15%, #0891B2 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #1D4ED8 15%, #0E7490 100%)', transform: 'translateY(-2px) scale(1.02)' },
                boxShadow: '0 18px 36px rgba(37, 99, 235, 0.28)',
                transition: 'box-shadow 0.2s, transform 0.2s',
                p: 0,
              }}
            >
              <AnimatedChatbotIcon active={!open} />
            </Fab>
          </Badge>
        </Box>
      )}

      {/* Chat Dialog */}
      <Dialog open={open} onClose={handleClose} TransitionComponent={Transition}
        PaperProps={{ sx: { position: 'fixed', bottom: 20, right: 20, m: 0, width: { xs: '92vw', sm: 420 }, height: { xs: '72vh', sm: 560 }, maxHeight: '82vh', zIndex: 2000, borderRadius: '24px', overflow: 'hidden', boxShadow: '0 28px 70px rgba(15, 23, 42, 0.24)', border: '1px solid rgba(148, 163, 184, 0.2)', background: '#F8FBFF' } }}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #0F3D91 0%, #2563EB 55%, #38BDF8 100%)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.18)', width: 42, height: 42 }}><BotIcon /></Avatar>
            <Box><Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>Aalay Assistant</Typography><Typography variant="caption" sx={{ opacity: 0.9 }}>{isTyping ? 'Typing...' : 'Online now'}</Typography></Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' } }}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent sx={{
          flex: 1, p: 1.25, background: 'linear-gradient(180deg, #F8FBFF 0%, #EEF6FF 100%)',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: '#eee', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb': { background: '#1976D2', borderRadius: '3px' }
        }} dividers>
          <Box sx={{ flex: 1, overflowY: 'auto', py: 1, px: 0.5 }}>
            {messages.map((m) => (
              <Box key={m.id} sx={{ display: 'flex', justifyContent: m.sender === 'user' ? 'flex-end' : 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: m.sender === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 1, maxWidth: '86%' }}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: m.sender === 'user' ? '#2563EB' : '#0F3D91', boxShadow: '0 6px 14px rgba(37, 99, 235, 0.18)' }}>
                    {m.sender === 'user' ? <PersonIcon /> : <BotIcon />}
                  </Avatar>
                  <Paper sx={{ p: 1.5, background: m.sender === 'user' ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)' : 'rgba(255,255,255,0.88)', color: m.sender === 'user' ? 'white' : '#0F172A', borderRadius: m.sender === 'user' ? '18px 18px 6px 18px' : '18px 18px 18px 6px', border: m.sender === 'user' ? 'none' : '1px solid rgba(148,163,184,0.16)', boxShadow: m.sender === 'user' ? '0 12px 28px rgba(37, 99, 235, 0.22)' : '0 10px 24px rgba(15, 23, 42, 0.08)', backdropFilter: 'blur(10px)' }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.55 }}>{m.text}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', opacity: m.sender === 'user' ? 0.82 : 0.6, mt: 0.6 }}>{formatTime(m.timestamp)}</Typography>
                    {m.sender === 'bot' && !m.isQuestion && (<>{renderServiceTiles()}{renderCategoryButtons()}{renderSuggestedQuestions()}</>)}
                  </Paper>
                </Box>
              </Box>
            ))}
            {isTyping && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}><Avatar sx={{ width: 34, height: 34, bgcolor: '#0F3D91' }}><BotIcon /></Avatar><Paper sx={{ px: 1.5, py: 1, borderRadius: '18px 18px 18px 6px', bgcolor: 'rgba(255,255,255,0.92)', border: '1px solid rgba(148,163,184,0.16)' }}><Typography variant="body2" sx={{ color: '#475569' }}>Typing...</Typography></Paper></Box>}
            <div ref={messagesEndRef} />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 1.5, backgroundColor: 'rgba(255,255,255,0.94)', borderTop: '1px solid rgba(148,163,184,0.14)' }}>
          <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
            <TextField fullWidth placeholder="Ask about warranties, AMC, complaints..." value={inputMessage} onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSendMessage(); } }} size="small" />
            <IconButton onClick={handleSendMessage} disabled={!inputMessage.trim()}
              sx={{
                bgcolor: inputMessage.trim() ? '#2563EB' : '#CBD5E1',
                color: 'white',
                width: 42,
                height: 42,
                borderRadius: '14px',
                boxShadow: inputMessage.trim() ? '0 10px 22px rgba(37, 99, 235, 0.25)' : 'none',
                '&:hover': { bgcolor: '#1D4ED8' },
                mb: 0.5,
              }}>
              <SendIcon />
            </IconButton>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FloatingChatbot;
